import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import type { UserDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Divider } from "@/components/Divider/Divider";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { getDicoStatus, getHomeDnaStatus, getRemsStatus, getWunderbricksStatus } from "@/components/Ticket/helpers";
import type { TicketCommentPayload } from "@/components/Ticket/TicketCommentField/helpers";
import { TicketDicoCard } from "@/components/Ticket/TicketDetails/TicketDicoCard";
import { TicketHistoryCard } from "@/components/Ticket/TicketDetails/TicketHistoryCard";
import { TicketHomeDnaCard } from "@/components/Ticket/TicketDetails/TicketHomeDnaCard";
import { TicketInfo } from "@/components/Ticket/TicketDetails/TicketInfoHeader";
import { TicketLog } from "@/components/Ticket/TicketDetails/TicketLog";
import { TicketReminderButton } from "@/components/Ticket/TicketDetails/TicketReminderButton";
import { TicketRemsCard } from "@/components/Ticket/TicketDetails/TicketRemsCard";
import { TicketWunderbricksCard } from "@/components/Ticket/TicketDetails/TicketWunderbricksCard";
import { TicketLeaveCommentModal } from "@/components/Ticket/TicketLeaveCommentModal";
import { TicketStatusChangeModal } from "@/components/Ticket/TicketStatusChangeModal";
import { UserDetailsContent } from "@/components/UserDetailsContent/UserDetailsContent";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { isHttpError } from "@/helpers/Network/errors";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { getLocalStorageValue, useUpdateLocalStorage } from "@/hooks/useLocalStorage";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { ProjectLoader } from "@/providers/ProjectLoader";
import { SessionUserLoader } from "@/providers/SessionUserLoader";
import { portfolioTicketsMutations } from "@/queries/portfolio-tickets/mutations";
import { canSeeServiceHistory, useTicketCommentHandler } from "@/queries/tickets/helpers";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

type PortfolioTicketDetailsModalProps = ModalBaseProps & {
  data?: {
    ticketId: string;
    projectId: string;
  };
  leftInSession: number | undefined;
  showNextTicketBtn: boolean;
  onNavigateNextTicket: () => void;
};

export function PortfolioTicketDetailsModal({
  isOpened,
  onOpenChange,
  data,
  leftInSession,
  showNextTicketBtn,
  onNavigateNextTicket,
}: PortfolioTicketDetailsModalProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Modal.Root
      title={
        showNextTicketBtn && leftInSession != undefined
          ? t("page.portfolio.tickets.details.title-session", { count: leftInSession })
          : t("page.portfolio.tickets.details.title")
      }
      size="fullscreen"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body withPadding={false}>
        {data ? (
          <ProjectLoader projectId={data.projectId}>
            <SessionUserLoader>
              <PortfolioTicketDetailsContent ticketId={data.ticketId} />
            </SessionUserLoader>
          </ProjectLoader>
        ) : (
          <FullSizeLoader noDelay />
        )}
      </Modal.Body>
      {showNextTicketBtn && (
        <Modal.Actions isSticky>
          <Button variant="filled" onClick={onNavigateNextTicket}>
            {t("page.portfolio.tickets.details.next-button")}
          </Button>
        </Modal.Actions>
      )}
    </Modal.Root>
  );
}

type PortfolioModalState = { assignee?: UserDto } & (
  | { type: "none" }
  | { type: "promptStatusChangeAfterComment" }
  | { type: "confirmAssigneeWarning"; assignee: UserDto }
  | { type: "leaveAssigneeChangeComment"; assignee: UserDto }
  | { type: "leaveStatusChangeCommentGeneral" }
);

interface PortfolioTicketDetailsContentProps {
  ticketId: string;
}

export function PortfolioTicketDetailsContent({ ticketId }: PortfolioTicketDetailsContentProps): React.ReactNode {
  const [modalState, setModalState] = useState<PortfolioModalState>({ type: "none" });

  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const [descendingActivities, setDescendingActivities] = useState(
    getLocalStorageValue(LOCAL_STORAGE_KEYS.TICKET_DESCENDING_ACTIVITIES, true),
  );

  const isAiTicketResponsibilitySuggestionAvailable = useConfig("isAiTicketResponsibilitySuggestionAvailable");
  useUpdateLocalStorage(LOCAL_STORAGE_KEYS.TICKET_DESCENDING_ACTIVITIES, descendingActivities);

  const ticketQueries = useTicketQueries();
  const {
    data: ticket,
    isPending: isLoadingTicket,
    error: ticketDetailsError,
  } = useQuery(ticketQueries.detailsAdmin(ticketId));
  const { isFetching: isFetchingResponsibilitySuggestion, isFetched: isFetchedResponsibilitySuggestion } = useQuery({
    ...ticketQueries.responsibilitySuggestion(ticketId),
    enabled: Boolean(ticket?.responsibilityAssignee) && isAiTicketResponsibilitySuggestionAvailable,
  });

  const activitiesQuery = useInfiniteQuery(
    ticketQueries.activitiesInfinite({
      ticketId,
      sortDescending: descendingActivities,
    }),
  );

  const { createComment, editComment } = useTicketCommentHandler(ticketId);
  const changeAssigneeMutation = ticketMutations.useUpdateAssignee();
  const changeStatusMutation = ticketMutations.useUpdateStatus();
  const markAsReadMutation = portfolioTicketsMutations.useMarkPortfolioTicketDetailsAsRead(ticketId);

  const activities = useMemo(
    () => activitiesQuery.data?.pages.flatMap((x) => x.items) || [],
    [activitiesQuery.data?.pages],
  );

  useEffect(() => {
    if (isHttpError(ticketDetailsError) && ticketDetailsError.status === 404) {
      void navigate(routes.tickets.list({ slug }));
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.errors.not-found"),
      });
    }
  }, [navigate, showFlashToast, t, ticketDetailsError, slug]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (ticket?.id && ticket.hasUnreadActivity) {
        markAsReadMutation.mutate();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [ticket?.id, ticket?.hasUnreadActivity, markAsReadMutation]);

  const onToggleActivitySorting = () => {
    setDescendingActivities((prev) => !prev);
  };

  const isFetchingMoreActivities = activitiesQuery.isFetchingNextPage || activitiesQuery.isPending;
  const hasMoreActivities = activitiesQuery.hasNextPage || false;
  const fetchMoreActivities = activitiesQuery.fetchNextPage;

  const bottomOfTicketLogRef = useRef<HTMLDivElement>(null);
  const statusesForModal = useMemo(
    () =>
      ticket?.possibleStatuses.map((status) => ({
        id: status.id,
        description: status.name,
        labelColor: status.color,
        defaultStatusId: status.type,
      })) || [],
    [ticket?.possibleStatuses],
  );
  const rems = ticket ? getRemsStatus(ticket) : undefined;
  const homeDna = ticket ? getHomeDnaStatus(ticket) : undefined;
  const dico = ticket ? getDicoStatus(ticket) : undefined;
  const wunderbricks = ticket ? getWunderbricksStatus(ticket) : undefined;

  const onCreateCommentInternal = async (payload: TicketCommentPayload) => {
    if (!ticket) {
      return;
    }

    await createComment({
      ...payload,
      fetchAllActivities: !descendingActivities,
    });

    if (!descendingActivities) {
      setTimeout(() => {
        bottomOfTicketLogRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 50);
    }

    const isStatusNewOrRejected = ["new", "rejected"].includes(ticket.status.type);
    if (payload.type !== "internal" && isStatusNewOrRejected && ticket.canChangeStatus) {
      setModalState({ type: "promptStatusChangeAfterComment" });
    }
  };

  const handleSubmitStatusChangeAfterComment = async (status: { id: string }) => {
    try {
      if (ticket && status.id !== ticket.status.id) {
        await changeStatusMutation.mutateAsync({
          ticketId,
          data: {
            statusId: status.id,
            silent: true,
          },
        });
      }
    } finally {
      setModalState({ type: "none" });
    }
  };

  const handleConfirmAssigneeWarning = async (confirmedAssignee?: UserDto) => {
    if (ticket && confirmedAssignee) {
      if (ticket.canAddInternalNote) {
        setModalState({ type: "leaveAssigneeChangeComment", assignee: confirmedAssignee });
      } else {
        await changeAssigneeMutation.mutateAsync({
          ticketId,
          data: { assigneeId: confirmedAssignee.id },
        });
        setModalState({ type: "none" });
      }
    } else {
      setModalState({ type: "none" });
    }
  };

  const handleSubmitAssigneeChangeWithNote = async (assignee?: UserDto, note?: string) => {
    if (assignee) {
      await changeAssigneeMutation.mutateAsync({
        ticketId,
        data: {
          assigneeId: assignee.id,
          note,
        },
      });
    }

    setModalState({ type: "none" });
  };

  const handleSubmitGeneralComment = async (value: string, isNote: boolean) => {
    try {
      await createComment({
        content: value,
        images: [],
        videos: [],
        documents: [],
        type: isNote ? "internal" : "public",
      });
    } finally {
      setModalState({ type: "none" });
    }
  };

  const error = ticketDetailsError || activitiesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoadingTicket || (!isFetchedResponsibilitySuggestion && isFetchingResponsibilitySuggestion) || !ticket) {
    return (
      <section className="flex h-screen max-h-screen-minus-16">
        <FullSizeLoader />
      </section>
    );
  }

  return (
    <Box padding="md">
      <Stack gap="md">
        {ticket.canSetReminder && (
          <nav className="flex justify-end @md:hidden">
            <TicketReminderButton ticket={ticket} />
          </nav>
        )}
        <PageGrid.Row>
          <PageGrid.Column size="66%">
            <article className="flex flex-1 flex-col gap-space-md">
              <TicketInfo ticket={ticket} showProject />
              <Divider />
              <TicketLog
                isActivitiesDescending={descendingActivities}
                onToggleActivitySorting={onToggleActivitySorting}
                isFetchingMoreActivities={isFetchingMoreActivities}
                fetchMoreActivities={fetchMoreActivities}
                hasMoreActivities={hasMoreActivities}
                onCreateComment={onCreateCommentInternal}
                onEditComment={editComment}
                {...{ ticket, sessionUser, activities }}
              />
            </article>
          </PageGrid.Column>
          <PageGrid.Column size="33%" isDesktopIndifferent>
            <aside className="flex w-full flex-col gap-space-md">
              {ticket.canSetReminder && (
                <nav className="hidden justify-end @md:flex">
                  <TicketReminderButton ticket={ticket} />
                </nav>
              )}

              <Box padding="md" background="secondary" radius="md">
                <Stack gap="md">
                  <h4 className="text-body-emphasis">{t("page.tickets.details.user.reporter")}</h4>
                  <UserDetailsContent
                    user={ticket.user}
                    canNavigate
                    showAddressDetails
                    address={{
                      building: ticket.address?.building.name,
                      floor: ticket.address?.floor,
                      apartmentType: ticket.address?.typeConstraint?.key,
                    }}
                  />
                </Stack>
              </Box>

              {canSeeServiceHistory(ticket, sessionUser) ? <TicketHistoryCard ticketId={ticket.id} /> : null}

              {rems ? (
                <TicketRemsCard ticketId={ticket.id} ticketIsCollective={ticket.visibility !== "private"} rems={rems} />
              ) : null}

              {homeDna ? <TicketHomeDnaCard ticket={ticket} homeDna={homeDna} /> : null}
              {dico ? <TicketDicoCard ticket={ticket} dico={dico} /> : null}
              {wunderbricks ? <TicketWunderbricksCard ticketId={ticket.id} wunderbricks={wunderbricks} /> : null}
            </aside>
          </PageGrid.Column>
        </PageGrid.Row>

        <ConfirmModal
          tone="warning"
          title={t("page.tickets.before-assignee-change-modal.title")}
          description={t("page.tickets.before-assignee-change-modal.description")}
          isLoading={changeAssigneeMutation.isPending}
          isOpened={modalState.type === "confirmAssigneeWarning"}
          onReject={() => setModalState({ type: "none" })}
          onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
          onResolve={() => modalState.assignee && handleConfirmAssigneeWarning(modalState.assignee)}
        />

        <TicketLeaveCommentModal
          ticket={ticket}
          title={t("page.tickets.after-status-change-modal.title")}
          description={
            <Trans
              i18nKey="page.tickets.after-status-change-modal.description"
              components={{ yellow: <span className="text-text-warning" /> }}
            />
          }
          canCommentInternal={ticket.canAddInternalNote}
          canCommentPublic={ticket.canAddPublicComment}
          isOpened={modalState.type === "leaveStatusChangeCommentGeneral"}
          onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
          onSubmit={handleSubmitGeneralComment}
        />

        <TicketLeaveCommentModal
          ticket={ticket}
          title={
            <Trans
              i18nKey="page.tickets.after-assignee-change-modal.title"
              values={{
                name: modalState.assignee?.fullName || "",
              }}
              components={{
                bold: <em className="italic" />,
              }}
            />
          }
          description={
            <Trans
              i18nKey="page.tickets.after-assignee-change-modal.description"
              components={{
                yellow: <span className="text-text-warning" />,
              }}
            />
          }
          canCommentInternal={ticket.canAddInternalNote}
          canCommentPublic={false}
          isOpened={modalState.type === "leaveAssigneeChangeComment"}
          onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
          onSubmit={(noteContent) => handleSubmitAssigneeChangeWithNote(modalState.assignee, noteContent)}
          assignee={modalState.assignee}
        />

        <TicketStatusChangeModal
          isOpened={modalState.type === "promptStatusChangeAfterComment"}
          onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
          onSubmit={handleSubmitStatusChangeAfterComment}
          targetStatus={statusesForModal[0]}
          possibleStatuses={statusesForModal}
        />
      </Stack>
    </Box>
  );
}
